import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import LazyLoad from "react-lazyload";
import LoadingAnim from "../../components/LoadingAnim";
import NoResult from "../../assets/inbox_empty.png";
import defaultImage from "../../assets/default.png";
import axios from "axios";
import { API_BASE_URL, API_KEY } from "../../config";
import TelegramBackButton from "../../components/TelegramBackButton.jsx";

const FilteredResults = () => {
  const location = useLocation();
  const {
    results: initialResults,
    totalCount: initialTotalCount,
    filters,
  } = location.state || {
    results: [],
    totalCount: 0,
    filters: {},
  };
  const {
    selectedCity,
    selectedPriceRange,
    isNearbySearch,
    location: userLocation,
    category,
    ITEMS_PER_PAGE,
  } = filters;

  const [displayedResults, setDisplayedResults] = useState(initialResults);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(2); // Start at page 2 as page 1 is already fetched
  const [hasMore, setHasMore] = useState(true);
  const [nextPageUrl, setNextPageUrl] = useState(null);

  const fetchFilteredData = useCallback(
    async (page, url = null) => {
      try {
        const requestUrl =
          url || `${API_BASE_URL}/user/api/v1/filter/?page=${page}`;
        const response = await axios.post(
          requestUrl,
          {
            city: selectedCity || null,
            minPrice: selectedPriceRange?.min || null,
            maxPrice: selectedPriceRange?.max || null,
            latitude: isNearbySearch ? userLocation.latitude : null,
            longitude: isNearbySearch ? userLocation.longitude : null,
            category: category || null,
            range: 10,
            page: page,
            page_size: ITEMS_PER_PAGE,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": API_KEY,
            },
          }
        );

        const newResults = response.data.results;
        setNextPageUrl(response.data.next);

        setHasMore(response.data.next !== null);
        return newResults;
      } catch (error) {
        console.error("Error fetching filtered results:", error);
        setHasMore(false);
        return [];
      }
    },
    [
      selectedCity,
      selectedPriceRange,
      isNearbySearch,
      userLocation,
      category,
      ITEMS_PER_PAGE,
    ]
  );

  const loadMoreResults = useCallback(async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    const newResults = await fetchFilteredData(currentPage, nextPageUrl);
    setDisplayedResults((prev) => [...prev, ...newResults]);
    if (newResults.length > 0) {
      setCurrentPage((prev) => prev + 1);
    }
    setIsLoading(false);
  }, [isLoading, hasMore, fetchFilteredData, currentPage, nextPageUrl]);

  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 200 &&
      hasMore
    ) {
      loadMoreResults();
    }
  }, [hasMore, loadMoreResults]);

  useEffect(() => {
    if (initialResults) {
      setDisplayedResults(initialResults);
    }
  }, [initialResults]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return (
    <div className="p-4">
      <TelegramBackButton />
      <h3 className="text-sm font-bold">Filtered Results</h3>

      {displayedResults.length > 0 ? (
        displayedResults.map((property, index) => (
          <div
            key={property.id || index}
            className="property-item rounded-lg overflow-hidden bg-white mb-6"
          >
            <Link
              to={`/GroupLists/${property.id}`}
              className="block"
              state={{ houses: [property] }}
            >
              <div className="property-image w-full">
                {property.tumble_image ? (
                  <LazyLoad height={350} offset={100}>
                    <img
                      src={property.tumble_image}
                      alt={`Property`}
                      className="w-full object-cover"
                      style={{ borderRadius: "15px", height: "350px" }}
                    />
                  </LazyLoad>
                ) : (
                  <img
                    src={defaultImage}
                    alt="Default"
                    className="w-full h-64 object-cover"
                    style={{ borderRadius: "15px", height: "350px" }}
                  />
                )}
              </div>

              <div className="p-1">
                <div className="flex justify-between items-center">
                  <h3 className="text-md font-bold truncate">
                    {property.houses[0].title}
                  </h3>
                  <div className="flex items-center">
                    <i className="fa fa-star" style={{ color: "#FFA500" }}></i>
                    <span className="px-1 text-xs text-gray-500">
                      {property?.rating}/5.0
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500">List all Amities</p>
                <p className="text-xs text-gray-500 mt-1">
                  Posted by <b>@{property?.userAccount?.first_name}</b>
                </p>

                <div className="flex justify-between mt-1">
                  <p className="text-xs text-gray-500 flex items-center">
                    <i className="fa fa-map-marker mr-1"></i>
                    {property.houses[0].city}
                  </p>
                  <p className="text-xs font-bold text-blue-500">
                    See all details
                  </p>
                </div>
              </div>
            </Link>
          </div>
        ))
      ) : (
        <div className="flex flex-col items-center justify-center text-center px-4 sm:px-0">
          <div className="flex flex-col justify-center items-center">
            <img
              src={NoResult}
              alt="no result found"
              className="mb-4 w-40 sm:w-22 md:w-30 lg:w-34 xl:w-32"
            />
          </div>
        </div>
      )}

      {isLoading && <LoadingAnim />}
      {!hasMore && displayedResults.length > 0 && (
        <p className="text-center text-gray-500 my-4">
          No more results to load.
        </p>
      )}
    </div>
  );
};

export default FilteredResults;
